import { useEffect, useState } from "react";

/**
 * Tracks the browser's network connectivity status.
 *
 * Uses the native `online` / `offline` events. Reads `navigator.onLine` for
 * the initial value, which is a hint (some platforms report stale state) —
 * any consumer that needs ground truth should still try the request and
 * fall back gracefully.
 *
 * SSR-safe: returns `true` on the server (no navigator).
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() => {
    if (typeof navigator === "undefined") return true;
    return navigator.onLine;
  });

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return online;
}
